// src/containers/ListingPage/CustomListingFields.js
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_ENUM } from '../../util/types';
import { pickCategoryFields, pickCustomFieldProps } from '../../util/fieldHelpers.js';
import { isThaiLocale } from '../../util/genericHelpers.js';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import {
  generateCrewRulesFieldProps,
  normalizeLongAndEnumFields,
} from '../../util/genericHelpers.js';
import css from './ListingPage.module.css';

// ✅ IMPORT THAI LABELS
import { FIELD_HEADING_TH, OPTION_LABEL_TH, GROUP_HEADING_TH } from '../../util/thaiFields';
import OpeningHours from './OpeningHours.js';

// -------------------- HELPERS --------------------

const localizeOptions = (options, intl, locale) => {
  if (!isThaiLocale(intl, locale) || !Array.isArray(options)) return options;

  return options.map(opt => ({
    ...opt,
    label: OPTION_LABEL_TH[opt.option] || opt.label,
  }));
};

const localizeFieldProps = (fieldProps, intl, locale) => {
  if (!fieldProps || !isThaiLocale(intl, locale)) return fieldProps;

  const key = fieldProps.key;
  const thaiHeading = FIELD_HEADING_TH[key];

  return {
    ...fieldProps,
    heading: thaiHeading || fieldProps.heading,
    label: thaiHeading || fieldProps.label,
    options: localizeOptions(fieldProps.options, intl, locale),
    enumOptions: localizeOptions(fieldProps.enumOptions, intl, locale),
  };
};

// -------------------- CONSTANTS --------------------

const CATEGORY_GROUPS = [
  {
    name: 'Amenities',
    keys: ['pub_amenities', 'amenity_services', 'amenity_accessibility', 'connectivity'],
  },
  {
    name: 'Studio Features',
    keys: ['floor', 'windows', 'wall', 'studio_and_production_features', 'studio_style'],
  },
  {
    name: 'Parking & Accessibility',
    keys: ['parking_accessibility'],
  },
  {
    name: '',
    keys: ['crew_rules'],
  },
  {
    name: '',
    keys: ['availability'],
  },
];

// -------------------- COMPONENT --------------------

const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl, locale } = props;
  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj).map(cat => cat.key || cat);

  const isFieldForSelectedCategories = fieldConfig => {
    const categoryIds = fieldConfig.categoryConfig?.categoryIds || [];
    return categoryIds.length === 0 || currentCategories.some(cat => categoryIds.includes(cat));
  };

  const normalizedPublicData = Object.keys(publicData).reduce((acc, key) => {
    const normalizedKey = key.startsWith('pub_') ? key.replace('pub_', '') : key;
    acc[normalizedKey] = publicData[key];
    return acc;
  }, {});

  const propsForCustomFields =
    pickCustomFieldProps(
      normalizedPublicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const formattedFields = normalizeLongAndEnumFields(propsForCustomFields, listingFieldConfigs);
  const crewRules = generateCrewRulesFieldProps(publicData, intl);

  // Always include crew_rules, with a fallback if no options
  formattedFields.push({
    ...crewRules,
    key: 'crew_rules',
    schemaType: SCHEMA_TYPE_MULTI_ENUM,
    label: 'Crew Rules',
    value: crewRules?.options?.length > 0 ? crewRules.value : null,
    text: crewRules?.options?.length > 0 ? null : 'No crew rules specified',
  });

  const fieldPropsByKey = formattedFields.reduce((acc, fieldProps) => {
    const config = listingFieldConfigs.find(cfg => cfg.key === fieldProps.key);
    acc[fieldProps.key] = {
      ...fieldProps,
      originalKey: fieldProps.key?.startsWith('pub_')
        ? `pub_${fieldProps.key.replace(/^pub_/, '')}`
        : fieldProps.key,
      enumOptions: config?.enumOptions || [],
    };
    if (!fieldProps.key?.startsWith('pub_')) {
      acc[`pub_${fieldProps.key}`] = acc[fieldProps.key];
    }
    return acc;
  }, {});

  const bookingTypeField =
    fieldPropsByKey['bookingType'] || fieldPropsByKey['pub_bookingType'];

  const hasBookingTypeSelected =
    Array.isArray(normalizedPublicData?.bookingType) &&
    normalizedPublicData.bookingType.length > 0;

  const renderCategoryGroup = (categoryGroup, index) => {
    const fieldsToRender = [];

    categoryGroup.keys.forEach(key => {
      if (key === 'bookingType' || key === 'pub_bookingType') return;
      let fieldProps = fieldPropsByKey[key] || fieldPropsByKey[key.replace('pub_', '')];
      if (!fieldProps) return;

      fieldProps = localizeFieldProps(fieldProps, intl, locale);

      const {
        schemaType,
        originalKey,
        label,
        text,
        enumOptions = [],
        key: _throwAwayKey,
        ...otherProps
      } = fieldProps;

      const normalizedKey = key.startsWith('pub_') ? key.replace('pub_', '') : key;
      const dataKey = originalKey ?? fieldProps.key ?? key;
      const fieldValue = normalizedPublicData[normalizedKey] ?? normalizedPublicData[dataKey];

      const isArrayField =
        schemaType === SCHEMA_TYPE_MULTI_ENUM || schemaType === SCHEMA_TYPE_ENUM;
      const hasValue = isArrayField
        ? Array.isArray(fieldValue) && fieldValue.length > 0
        : !!(fieldValue || text);

      if (!hasValue && key !== 'crew_rules') return;

      let valueToShow = fieldValue || text;

      if (
        schemaType === SCHEMA_TYPE_ENUM &&
        typeof valueToShow === 'string' &&
        enumOptions?.length
      ) {
        const match = enumOptions.find(opt => opt.option === valueToShow);
        valueToShow = match?.label || valueToShow;
      }

      if (schemaType === SCHEMA_TYPE_MULTI_ENUM || Array.isArray(fieldValue)) {
        fieldsToRender.push(
          <SectionMultiEnumMaybe
            key={dataKey}
            {...otherProps}
            value={fieldValue}
            label={label || normalizedKey}
            heading={fieldProps.heading || label}
            isListingPage
          />
        );
      } else {
        fieldsToRender.push(
          <SectionTextMaybe
            key={dataKey}
            {...otherProps}
            value={valueToShow}
            label={label || normalizedKey}
          />
        );
      }
    });

    if (fieldsToRender.length === 0) return null;

    return (
      <div key={index} className={css.categoryGroup}>
        {categoryGroup.name && (
          <h2 className={css.categoryHeader}>
            {isThaiLocale(intl, locale)
              ? GROUP_HEADING_TH[categoryGroup.name] || categoryGroup.name
              : categoryGroup.name}
          </h2>
        )}

        {fieldsToRender}
      </div>
    );
  };

  return (
    <>
      <SectionDetailsMaybe
        {...props}
        locale={props.locale || props.intl?.locale}
        isFieldForCategory={isFieldForSelectedCategories}
      />
      {CATEGORY_GROUPS.map(renderCategoryGroup).filter(Boolean)}
      {bookingTypeField && hasBookingTypeSelected && (
        <OpeningHours availability={publicData?.availabilityPlan} />
      )}
    </>
  );
};

export default CustomListingFields;
